import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  checkPersonalIdentityVerification,
  getPersonalCodeAuthError,
  loadPersonalLoginStatus,
  PERSONAL_AUTH_PROVIDERS,
  rotatePersonalLoginCode,
  startPersonalIdentityVerification,
} from '../api/personalCodeAuthApi.js';

const EMPTY_NOTICE = { type: '', message: '' };

function normalizedStatus(status) {
  const provider = status?.provider || (status?.gallog_id ? PERSONAL_AUTH_PROVIDERS.DC_GALLOG : '');
  const identityValue = status?.identity_value || status?.profile_url || status?.gallog_id || '';
  const identityLabel = status?.identity_label || status?.gallog_id || identityValue;
  return { provider, identityValue, identityLabel };
}

function providerLabel(provider) {
  return provider === PERSONAL_AUTH_PROVIDERS.ARCA ? '아카라이브' : 'DC 갤로그';
}

async function copyText(value) {
  const nextValue = String(value || '').trim();
  if (!nextValue) throw new Error('copy_value_missing');

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(nextValue);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = nextValue;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, nextValue.length);
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('clipboard_unavailable');
}

export function PersonalLoginCodeSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [notice, setNotice] = useState(EMPTY_NOTICE);
  const [newCode, setNewCode] = useState('');
  const [provider, setProvider] = useState(PERSONAL_AUTH_PROVIDERS.DC_GALLOG);
  const [identityValue, setIdentityValue] = useState('');
  const [challenge, setChallenge] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await loadPersonalLoginStatus());
    } catch (error) {
      setNotice({ type: 'error', message: getPersonalCodeAuthError(error) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const currentIdentity = useMemo(() => normalizedStatus(status), [status]);
  const currentCode = String(newCode || status?.login_code_value || '').trim();

  const rotate = useCallback(async () => {
    if (!window.confirm('새 로그인 코드를 발급하면 현재 기기를 포함한 기존 로그인은 모두 해제됩니다. 새 코드로 다시 로그인한 뒤 내 정보에서 계속 조회·복사할 수 있습니다. 계속할까요?')) return;
    setWorking('rotate');
    setNotice(EMPTY_NOTICE);
    try {
      await rotatePersonalLoginCode();
      // AUTH_SESSION_REBOOT_REQUIRED: the API clears Supabase auth persistence, so the app shell must boot again as signed out.
      window.location.reload();
    } catch (error) {
      setNotice({ type: 'error', message: getPersonalCodeAuthError(error) });
      setWorking('');
    }
  }, []);

  const changeProvider = useCallback((nextProvider) => {
    setProvider(nextProvider === PERSONAL_AUTH_PROVIDERS.ARCA ? PERSONAL_AUTH_PROVIDERS.ARCA : PERSONAL_AUTH_PROVIDERS.DC_GALLOG);
    setIdentityValue('');
    setChallenge(null);
    setNotice(EMPTY_NOTICE);
  }, []);

  const startLink = useCallback(async (event) => {
    event.preventDefault();
    setWorking('start');
    setNotice(EMPTY_NOTICE);
    try {
      const next = await startPersonalIdentityVerification(provider, identityValue);
      setChallenge({ ...next, provider: next.provider || provider });
      setNotice({
        type: 'success',
        message: provider === PERSONAL_AUTH_PROVIDERS.ARCA
          ? '아카라이브 프로필 상태 메시지에 인증코드를 입력해주세요.'
          : 'DC에 로그인한 본인 계정으로 갤로그 방명록에 인증코드를 작성해주세요.',
      });
    } catch (error) {
      setNotice({ type: 'error', message: getPersonalCodeAuthError(error) });
    } finally {
      setWorking('');
    }
  }, [identityValue, provider]);

  const verifyLink = useCallback(async () => {
    if (!challenge?.challengeId) return;
    setWorking('verify');
    setNotice(EMPTY_NOTICE);
    try {
      const result = await checkPersonalIdentityVerification(challenge.challengeId, challenge.provider || provider);
      setNewCode(result.loginCode);
      setChallenge(null);
      await refresh();
      setNotice({ type: 'success', message: `${providerLabel(result.provider || provider)} 연결과 개인 로그인 코드 발급이 완료됐습니다.` });
    } catch (error) {
      setNotice({ type: 'error', message: getPersonalCodeAuthError(error) });
    } finally {
      setWorking('');
    }
  }, [challenge, provider, refresh]);

  const copy = useCallback(async (value) => {
    try {
      await copyText(value);
      setNotice({ type: 'success', message: '로그인 코드를 복사했습니다.' });
    } catch {
      setNotice({ type: 'error', message: '복사하지 못했습니다. 코드를 길게 눌러 직접 복사해주세요.' });
    }
  }, []);

  if (loading) return <div className="personal-login-settings-state">개인 로그인 상태 확인 중…</div>;

  const challengeProvider = challenge?.provider || provider;
  const challengeIsArca = challengeProvider === PERSONAL_AUTH_PROVIDERS.ARCA;
  const verificationUrl = challenge?.verificationUrl || challenge?.profileUrl || challenge?.guestbookUrl;

  return (
    <section className="personal-login-settings" aria-label="개인 로그인 코드 관리">
      <header>
        <h1>개인 로그인 코드</h1>
        <p>이메일 대신 사용하는 DoroParty 서버 발급 코드입니다. 본인 내 정보 화면에서 언제든 다시 조회하고 복사할 수 있습니다.</p>
      </header>

      {currentIdentity.identityValue ? (
        <>
          <div className="personal-login-summary">
            <article><span>인증 서비스</span><strong>{providerLabel(currentIdentity.provider)}</strong></article>
            <article><span>연결된 공개 프로필</span><strong>{currentIdentity.identityLabel}</strong></article>
            <article className="personal-login-code-hint">
              <span>현재 로그인 코드</span>
              {currentCode ? (
                <div className="personal-login-summary-code">
                  <strong>{currentCode}</strong>
                  <button type="button" aria-label="현재 로그인 코드 복사" onClick={() => copy(currentCode)}>복사</button>
                </div>
              ) : (
                <strong>{status.login_code_hint ? '재발급 필요' : '발급 필요'}</strong>
              )}
              <small>{currentCode ? '본인 계정에서만 조회됩니다. 다른 기기 로그인에 그대로 사용할 수 있습니다.' : status.login_code_hint ? '기존 코드 원문은 복구할 수 없습니다. 한 번 재발급하면 이후 계속 조회할 수 있습니다.' : '개인 로그인 코드를 발급해주세요.'}</small>
            </article>
            <article><span>마지막 재발급</span><strong>{status.login_code_rotated_at ? new Date(status.login_code_rotated_at).toLocaleString('ko-KR') : '-'}</strong></article>
          </div>

          <button className="personal-login-rotate" type="button" disabled={Boolean(working)} onClick={rotate}>
            {working === 'rotate' ? '재발급 중…' : status.login_code_hint ? '로그인 코드 재발급' : '로그인 코드 발급'}
          </button>
        </>
      ) : challenge ? (
        <div className="personal-login-link-challenge">
          <span>{challengeIsArca ? '상태 메시지 인증코드' : '방명록 인증코드'}</span><strong>{challenge.verificationCode}</strong>
          <a href={verificationUrl} target="_blank" rel="noreferrer">{challengeIsArca ? '아카라이브 프로필 열기' : '갤로그 방명록 열기'}</a>
          <p>{challengeIsArca ? '프로필 상태 메시지에 인증코드를 그대로 입력하고 저장한 뒤 확인하세요.' : 'DC에 로그인한 본인 계정으로 방명록에 인증코드를 작성하세요. 비로그인 글은 인증되지 않으며 작성자 갤로그를 대조합니다.'}</p>
          <button type="button" disabled={Boolean(working)} onClick={verifyLink}>{working === 'verify' ? '확인 중…' : '인증 확인'}</button>
        </div>
      ) : (
        <form className="personal-login-link-form" onSubmit={startLink}>
          <strong>기존 계정에 공개 프로필 인증을 연결해주세요.</strong>
          <p>연결 후 개인 로그인 코드를 발급하면 다음 로그인부터 이메일이 필요하지 않습니다.</p>
          <div className="personal-login-provider-tabs" role="tablist" aria-label="본인 인증 서비스 선택">
            <button type="button" role="tab" aria-selected={provider === PERSONAL_AUTH_PROVIDERS.DC_GALLOG} className={provider === PERSONAL_AUTH_PROVIDERS.DC_GALLOG ? 'active' : ''} onClick={() => changeProvider(PERSONAL_AUTH_PROVIDERS.DC_GALLOG)}>DC 갤로그</button>
            <button type="button" role="tab" aria-selected={provider === PERSONAL_AUTH_PROVIDERS.ARCA} className={provider === PERSONAL_AUTH_PROVIDERS.ARCA ? 'active' : ''} onClick={() => changeProvider(PERSONAL_AUTH_PROVIDERS.ARCA)}>아카라이브</button>
          </div>
          <label><span>{provider === PERSONAL_AUTH_PROVIDERS.ARCA ? '아카라이브 닉네임 또는 프로필 주소' : 'DC 갤로그 식별코드'}</span><input type="text" value={identityValue} onChange={(event) => setIdentityValue(event.target.value)} placeholder={provider === PERSONAL_AUTH_PROVIDERS.ARCA ? '닉네임 입력 또는 https://arca.live/u/@닉네임/숫자' : '식별코드 입력'} required /></label>
          <p>{provider === PERSONAL_AUTH_PROVIDERS.ARCA ? '일반 고닉은 닉네임 또는 프로필 주소를 입력할 수 있고, 반고닉은 숫자 식별자가 붙은 프로필 주소를 그대로 입력하세요.' : '갤로그가 공개 상태여야 하며, 방명록 글은 반드시 DC에 로그인한 본인 계정으로 작성해야 합니다.'}</p>
          <button type="submit" disabled={Boolean(working)}>{working === 'start' ? (provider === PERSONAL_AUTH_PROVIDERS.ARCA ? '프로필 확인 중…' : '갤로그 확인 중…') : '인증코드 발급'}</button>
        </form>
      )}

      {notice.message ? <p className={`personal-login-notice ${notice.type}`} role="status">{notice.message}</p> : null}
    </section>
  );
}
